import ErrorState from '@pinecast/common/ErrorState';
import Link from '@pinecast/common/Link';
import LoadingState from '@pinecast/common/LoadingState';
import Section from '@pinecast/common/Section';
import {DashboardTitle, P} from '@pinecast/common/Text';
import {usePageTitleEffect} from '@pinecast/db-spa/App/usePageTitle';
import {useQuery} from '@pinecast/gql';
import type {
  DonationSuccessPage as DonationSuccessPageQuery,
  DonationSuccessPageVariables,
} from '@pinecast/gql/__generated_tj__/DonationSuccessPage';
import {defineMessage, FormattedMessage} from '@pinecast/i18n';
import {useCSS} from '@pinecast/styles';
import gql from 'graphql-tag';
import {useParams} from 'react-router-dom';

import DonationPageHeader from './DonationPageHeader';

const messages = {
  title: defineMessage({
    description:
      'Title for podcast donation success page. {podcast} is the name of the podcast',
    defaultMessage: 'Thanks for supporting {podcast}',
  }),
};

const DONATION_SUCCESS_PAGE = gql`
  query DonationSuccessPage($slug: ID!) {
    podcast(slug: $slug) {
      slug
      name
      coverArtThumbnail(size: 128)
      hasPrivateEpisodes
      tipJarSuccessMessage
    }
    tipJarSubscription(podcastSlug: $slug) {
      id
      eligibleForPrivateContent
    }
  }
`;

const DonationPage = () => {
  const params = useParams<{slug: string}>();

  // React Router v6 useParams can return undefined values
  if (!params.slug) {
    throw new Error('Missing required route parameter: slug');
  }
  const css = useCSS();
  const query = useQuery<
    DonationSuccessPageQuery,
    DonationSuccessPageVariables
  >(DONATION_SUCCESS_PAGE, {variables: {slug: params.slug}});

  usePageTitleEffect(() => {
    if (!query.data?.podcast) {
      return;
    }
    return [messages.title, {podcast: query.data.podcast.name}];
  }, [query]);

  return (
    <div
      className={css({
        margin: '20px auto',
        maxWidth: '720px',
        padding: '0 12px',
      })}
    >
      <DonationPageHeader
        slug={params.slug}
        podcast={query.data?.podcast ?? null}
      />
      {query.loading ? (
        <Section>
          <LoadingState
            title={
              <FormattedMessage
                description="Message for when the donation page content is loading"
                defaultMessage="Loading podcast information…"
              />
            }
          />
        </Section>
      ) : query.error ? (
        <Section>
          <ErrorState
            title={
              <FormattedMessage
                description="Message for when the donation page content has errored"
                defaultMessage="Unable to load podcast"
              />
            }
          />
        </Section>
      ) : (
        <>
          <Section>
            <div
              className={css({
                display: 'flex',
                fontSize: '64px',
                padding: '40px 0 20px',
                justifyContent: 'center',
              })}
            >
              {'🎉'}
            </div>
            <div
              className={css({
                fontSize: '16px',
                textAlign: 'center',
                padding: '20px',
              })}
            >
              {query.data.podcast!.tipJarSuccessMessage || (
                <FormattedMessage
                  description="Message shown when there is no configured success message for subscribing to a podcast"
                  defaultMessage="Thanks for subscribing! Your patronage makes this podcast possible."
                />
              )}
            </div>
          </Section>
          {query.data.podcast!.hasPrivateEpisodes &&
            query.data.tipJarSubscription?.eligibleForPrivateContent && (
              <Section>
                <DashboardTitle>
                  <FormattedMessage
                    description="Heading for instructions on subscribing to private content"
                    defaultMessage="Private podcast content"
                  />
                </DashboardTitle>
                <P>
                  <FormattedMessage
                    description="Copy for instructions on subscribing to private content. {podcast} is the name of the podcast"
                    defaultMessage="Your monthly contribution makes you eligible to listen to private content from {podcast}. Visit your <Link>contribution dashboard</Link> to get started."
                    values={{
                      podcast: query.data.podcast!.name,

                      Link: (...c) => (
                        <Link
                          href={`/subscriptions/recurring_tips/${
                            query.data.podcast!.slug
                          }`}
                        >
                          {c}
                        </Link>
                      ),
                    }}
                  />
                </P>
              </Section>
            )}
        </>
      )}
    </div>
  );
};

export default DonationPage;
